import {
	json,
	type MetaFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { useLoaderData, Link, useSearchParams } from '@remix-run/react'
import {
	Search,
	Calendar,
	MapPin,
	Zap,
	Music,
	Check,
	ChevronsUpDown,
} from 'lucide-react'
import { useState } from 'react'
import {
	Container,
	H1,
	H2,
	H3,
	P,
	Button,
	Card,
	CardContent,
	Badge,
	Input,
	Progress,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '#app/ui/components'
import { appName } from '#app/utils/constants.ts'
import { prisma } from '#app/utils/db.server.ts'
import { isEventOccurrenceBookableByDate } from '#app/utils/ticket.server.ts'
import {
	formatInTimezone,
	getTimezoneAbbreviation,
} from '#app/utils/timezone.ts'

export const meta: MetaFunction = () => [
	{ title: `${appName} - Discover Amazing Night Events` },
	{
		name: 'description',
		content:
			'Find and book tickets for the hottest nightlife events in your city. Exclusive clubs, live music, and unforgettable nights await!',
	},
]

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const searchQuery = url.searchParams.get('search') || ''
	const genreFilter = url.searchParams.get('genre') || ''
	const cityFilter = url.searchParams.get('city') || ''

	// Build the where clause dynamically
	const eventWhereClause: any = {}

	if (searchQuery) {
		eventWhereClause.OR = [
			{ name: { contains: searchQuery } },
			{ description: { contains: searchQuery } },
			{ nightclub: { name: { contains: searchQuery } } },
		]
	}

	if (genreFilter) {
		eventWhereClause.genres = {
			some: {
				name: { equals: genreFilter },
			},
		}
	}

	if (cityFilter) {
		eventWhereClause.nightclub = {
			address: { contains: cityFilter, mode: 'insensitive' },
		}
	}

	// Get all upcoming and ongoing event occurrences with comprehensive data
	const eventOccurrences = await prisma.eventOccurrence.findMany({
		where: {
			endDate: {
				gte: new Date(), // Include events that haven't ended yet
			},
			event: eventWhereClause,
		},
		include: {
			event: {
				include: {
					nightclub: {
						select: {
							id: true,
							name: true,
							address: true,
							latitude: true,
							longitude: true,
							capacity: true,
							timezone: true,
						},
					},
					genres: {
						select: {
							name: true,
						},
					},
					images: {
						select: {
							id: true,
							altText: true,
						},
						take: 1,
					},
					ticketTypes: {
						select: {
							id: true,
							name: true,
							defaultQuantity: true,
							isPaid: true,
							basePrice: true,
							_count: {
								select: {
									purchasedTickets: {
										where: {
											purchase: {
												status: 'completed',
											},
										},
									},
								},
							},
						},
					},
				},
			},
		},
		orderBy: [{ startDate: 'asc' }],
		take: 50, // Limit for performance
	})

	// Process events with ticket availability
	const eventsWithAvailability = await Promise.all(
		eventOccurrences.map(async (occurrence: any) => {
			const isBookable = isEventOccurrenceBookableByDate(
				occurrence.startDate,
				occurrence.endDate,
				occurrence.cutoffHours,
			)
			const capacity =
				occurrence.capacity || occurrence.event.nightclub.capacity

			// Calculate ticket availability from event-level ticket types
			const ticketStats = occurrence.event.ticketTypes.reduce(
				(stats: any, ticketType: any) => {
					const sold = ticketType._count.purchasedTickets
					const available = ticketType.defaultQuantity - sold

					stats.totalAvailable += ticketType.defaultQuantity
					stats.totalSold += sold
					stats.availableNow += available

					if (ticketType.isPaid) {
						stats.minPrice =
							stats.minPrice === null
								? ticketType.basePrice
								: Math.min(stats.minPrice!, ticketType.basePrice || 0)
						stats.maxPrice = Math.max(stats.maxPrice, ticketType.basePrice || 0)
						stats.hasPaidTickets = true
					} else {
						stats.hasFreeTickets = true
					}

					return stats
				},
				{
					totalAvailable: 0,
					totalSold: 0,
					availableNow: 0,
					minPrice: null as number | null,
					maxPrice: 0,
					hasPaidTickets: false,
					hasFreeTickets: false,
				},
			)

			const utilizationPercentage =
				ticketStats.totalAvailable > 0
					? Math.round(
							(ticketStats.totalSold / ticketStats.totalAvailable) * 100,
						)
					: 0

			return {
				...occurrence,
				isBookable,
				capacity,
				ticketStats,
				utilizationPercentage,
				hasTickets: occurrence.event.ticketTypes.length > 0,
				isSoldOut:
					ticketStats.availableNow === 0 &&
					occurrence.event.ticketTypes.length > 0,
			}
		}),
	)

	// Filter out non-bookable events and get stats
	const bookableEvents = eventsWithAvailability.filter(
		(event: any) => event.isBookable,
	)

	// Get unique genres and cities for filters
	const allGenres = await prisma.genres.findMany({
		orderBy: { name: 'asc' },
	})

	const allCities = await prisma.nightclub.findMany({
		select: { address: true },
		distinct: ['address'],
	})

	// Extract city names (simplified)
	const cities = allCities
		.map((nightclub) => {
			const city =
				nightclub.address.split(',').pop()?.trim() || nightclub.address
			return city
		})
		.filter((city, index, self) => self.indexOf(city) === index)

	// Calculate platform stats
	const liveEvents = bookableEvents.filter((event) => {
		const now = new Date()
		const startDate = new Date(event.startDate)
		const endDate = new Date(event.endDate)
		const cutoffTimeMs = endDate.getTime() - event.cutoffHours * 60 * 60 * 1000
		return startDate <= now && endDate > now && now < new Date(cutoffTimeMs)
	})

	const stats = {
		totalEvents: bookableEvents.length,
		liveEvents: liveEvents.length,
		totalVenues: new Set(bookableEvents.map((e: any) => e.event.nightclub.id))
			.size,
		totalGenres: allGenres.length,
		totalTicketsAvailable: bookableEvents.reduce(
			(sum: number, e: any) => sum + e.ticketStats.availableNow,
			0,
		),
	}

	return json({
		events: bookableEvents,
		genres: allGenres,
		cities: cities.sort(),
		stats,
		filters: {
			search: searchQuery,
			genre: genreFilter,
			city: cityFilter,
		},
	})
}

export default function Index() {
	const { events, genres, cities, stats, filters } =
		useLoaderData<typeof loader>()
	const [searchParams, setSearchParams] = useSearchParams()
	const [searchInput, setSearchInput] = useState(filters.search)
	const [genreOpen, setGenreOpen] = useState(false)
	const [cityOpen, setCityOpen] = useState(false)

	// Update search params
	const updateFilter = (key: string, value: string) => {
		const newParams = new URLSearchParams(searchParams)
		if (value && !value.startsWith('all-')) {
			newParams.set(key, value)
		} else {
			newParams.delete(key)
		}
		setSearchParams(newParams)
	}

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault()
		updateFilter('search', searchInput)
	}

	const formatDate = (dateString: string | Date, timezone: string) => {
		const date = new Date(dateString)
		return {
			dayMonth: formatInTimezone(date, timezone, {
				month: 'short',
				day: 'numeric',
			}),
			weekday: formatInTimezone(date, timezone, {
				weekday: 'short',
			}),
			time: formatInTimezone(date, timezone, {
				hour: '2-digit',
				minute: '2-digit',
			}),
			full: formatInTimezone(date, timezone, {
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			}),
			timezone: getTimezoneAbbreviation(timezone, date),
		}
	}

	const getPriceDisplay = (event: (typeof events)[0]) => {
		const { minPrice, maxPrice, hasPaidTickets, hasFreeTickets } =
			event.ticketStats

		if (!hasPaidTickets && hasFreeTickets) return 'Free'
		if (!hasPaidTickets && !hasFreeTickets) return 'No tickets'
		if (minPrice === maxPrice) return `€${minPrice}`
		return `€${minPrice} - €${maxPrice}`
	}

	const getEventStatus = (event: (typeof events)[0]) => {
		const now = new Date()
		const startDate = new Date(event.startDate)
		const endDate = new Date(event.endDate)
		const isOngoing = startDate <= now && now < endDate

		// Check if still bookable (using the same logic as our server function)
		const cutoffTimeMs = endDate.getTime() - event.cutoffHours * 60 * 60 * 1000
		const cutoffTime = new Date(cutoffTimeMs)
		const isStillBookable = now < cutoffTime

		if (isOngoing && isStillBookable) {
			return {
				text: 'LIVE NOW',
				color: 'red',
				variant: 'default' as const,
				priority: 1,
				isLive: true,
				description:
					"Event is happening now! Book your tickets before it's too late",
			}
		}

		if (!event.hasTickets) {
			return {
				text: 'No tickets',
				color: 'gray',
				variant: 'secondary' as const,
				priority: 6,
				isLive: false,
				description: "This event doesn't offer tickets",
			}
		}

		if (event.isSoldOut) {
			return {
				text: 'Sold Out',
				color: 'red',
				variant: 'destructive' as const,
				priority: 5,
				isLive: false,
				description: 'All tickets have been sold',
			}
		}

		if (event.ticketStats.availableNow < 10) {
			return {
				text: 'Few left',
				color: 'orange',
				variant: 'secondary' as const,
				priority: 3,
				isLive: false,
				description: `Only ${event.ticketStats.availableNow} tickets remaining`,
			}
		}

		if (event.utilizationPercentage > 80) {
			return {
				text: 'Selling fast',
				color: 'yellow',
				variant: 'secondary' as const,
				priority: 4,
				isLive: false,
				description: 'Tickets are selling quickly',
			}
		}

		return {
			text: 'Available',
			color: 'green',
			variant: 'default' as const,
			priority: 5,
			isLive: false,
			description: 'Tickets are available for purchase',
		}
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
			{/* Hero Section */}
			<section className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-purple-700 to-blue-600">
				<Container className="relative py-20 lg:py-32">
					<div className="text-center text-white">
						<div className="mb-6 flex justify-center">
							<div className="flex items-center space-x-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur">
								<Zap className="h-5 w-5 text-yellow-300" />
								<span className="text-sm font-medium">
									Live Events Platform
								</span>
							</div>
						</div>
						<H1 className="mb-6 text-4xl font-bold text-white lg:text-6xl">
							Discover Amazing
							<br />
							<span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
								Night Events
							</span>
						</H1>
						<P className="mx-auto mb-8 max-w-2xl text-xl text-purple-100">
							Find and book tickets for the hottest nightlife events in your
							city. Exclusive clubs, live music, and unforgettable nights await!
						</P>

						{/* Platform Stats */}
						<div className="mx-auto mb-8 grid max-w-2xl grid-cols-2 gap-4 lg:grid-cols-4">
							{stats.liveEvents > 0 ? (
								<div className="rounded-lg border border-red-400/30 bg-gradient-to-r from-red-600/20 to-red-500/10 p-4 backdrop-blur">
									<div className="mb-1 flex items-center justify-center space-x-2">
										<div className="text-2xl font-bold text-white">
											{stats.liveEvents}
										</div>
										<div className="h-2 w-2 animate-pulse rounded-full bg-red-400"></div>
									</div>
									<div className="text-center text-sm text-red-100">
										Live Now
									</div>
								</div>
							) : (
								<div className="rounded-lg bg-white/10 p-4 backdrop-blur">
									<div className="text-2xl font-bold text-white">
										{stats.totalEvents}
									</div>
									<div className="text-sm text-purple-200">Total Events</div>
								</div>
							)}
							<div className="rounded-lg bg-white/10 p-4 backdrop-blur">
								<div className="text-2xl font-bold text-white">
									{stats.totalVenues}
								</div>
								<div className="text-sm text-purple-200">Venues</div>
							</div>
							<div className="rounded-lg bg-white/10 p-4 backdrop-blur">
								<div className="text-2xl font-bold text-white">
									{stats.totalGenres}
								</div>
								<div className="text-sm text-purple-200">Genres</div>
							</div>
							<div className="rounded-lg bg-white/10 p-4 backdrop-blur">
								<div className="text-2xl font-bold text-white">
									{stats.totalTicketsAvailable.toLocaleString()}
								</div>
								<div className="text-sm text-purple-200">Tickets Available</div>
							</div>
						</div>
					</div>
				</Container>
			</section>

			{/* Search and Filters */}
			<section className="border-b bg-white/80 backdrop-blur-sm">
				<Container className="py-8">
					<div className="flex flex-col space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
						{/* Search */}
						<form onSubmit={handleSearch} className="flex flex-1 space-x-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
								<Input
									type="text"
									placeholder="Search events, venues, or artists..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									className="pl-10"
								/>
							</div>
							<Button type="submit">Search</Button>
						</form>

						{/* Filters */}
						<div className="flex space-x-2">
							<Popover open={genreOpen} onOpenChange={setGenreOpen}>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={genreOpen}
										className="w-40 justify-between"
									>
										{filters.genre
											? genres.find((genre) => genre.name === filters.genre)
													?.name
											: 'All Genres'}
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-60 p-0">
									<Command>
										<CommandInput placeholder="Search genres..." />
										<CommandList>
											<CommandEmpty>No genre found.</CommandEmpty>
											<CommandGroup>
												<CommandItem
													value="all-genres"
													onSelect={() => {
														updateFilter('genre', '')
														setGenreOpen(false)
													}}
												>
													<Check
														className={`mr-2 h-4 w-4 ${
															!filters.genre ? 'opacity-100' : 'opacity-0'
														}`}
													/>
													All Genres
												</CommandItem>
												{genres.map((genre) => (
													<CommandItem
														key={genre.id}
														value={genre.name}
														onSelect={(currentValue) => {
															updateFilter('genre', currentValue)
															setGenreOpen(false)
														}}
													>
														<Check
															className={`mr-2 h-4 w-4 ${
																filters.genre === genre.name
																	? 'opacity-100'
																	: 'opacity-0'
															}`}
														/>
														{genre.name}
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>

							<Popover open={cityOpen} onOpenChange={setCityOpen}>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={cityOpen}
										className="w-40 justify-between"
									>
										{filters.city
											? cities.find((city) => city === filters.city)
											: 'All Cities'}
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-60 p-0">
									<Command>
										<CommandInput placeholder="Search cities..." />
										<CommandList>
											<CommandEmpty>No city found.</CommandEmpty>
											<CommandGroup>
												<CommandItem
													value="all-cities"
													onSelect={() => {
														updateFilter('city', '')
														setCityOpen(false)
													}}
												>
													<Check
														className={`mr-2 h-4 w-4 ${
															!filters.city ? 'opacity-100' : 'opacity-0'
														}`}
													/>
													All Cities
												</CommandItem>
												{cities.map((city) => (
													<CommandItem
														key={city}
														value={city}
														onSelect={(currentValue) => {
															updateFilter('city', currentValue)
															setCityOpen(false)
														}}
													>
														<Check
															className={`mr-2 h-4 w-4 ${
																filters.city === city
																	? 'opacity-100'
																	: 'opacity-0'
															}`}
														/>
														{city}
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
						</div>
					</div>
				</Container>
			</section>

			{/* Events Grid */}
			<section className="py-12">
				<Container>
					{events.length === 0 ? (
						<div className="py-16 text-center">
							<Music className="mx-auto mb-4 h-16 w-16 text-gray-400" />
							<H2 className="mb-4 text-2xl font-bold text-gray-900">
								No events found
							</H2>
							<P className="mx-auto mb-6 text-gray-600">
								Try adjusting your search or filter criteria to find more
								events.
							</P>
							<Button asChild variant="outline">
								<Link to="/" onClick={() => setSearchParams({})}>
									Clear Filters
								</Link>
							</Button>
						</div>
					) : (
						<>
							{/* Live Events Section */}
							{(() => {
								const liveEvents = events.filter(
									(event) => getEventStatus(event).isLive,
								)
								if (liveEvents.length === 0) return null

								return (
									<div className="mb-12">
										<div className="mb-6 flex items-center space-x-3">
											<div className="flex items-center space-x-2">
												<div className="h-3 w-3 animate-pulse rounded-full bg-red-500"></div>
												<H2 className="bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-2xl font-bold text-transparent">
													Live Events Happening Now
												</H2>
											</div>
											<Badge
												variant="secondary"
												className="bg-red-100 text-red-700"
											>
												{liveEvents.length} live
											</Badge>
										</div>
										<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
											{liveEvents.slice(0, 3).map((event) => {
												const priceDisplay = getPriceDisplay(event)

												return (
													<Link
														key={`live-${event.id}`}
														to={`/events/${event.event.id}`}
														className="block"
													>
														<Card className="group cursor-pointer overflow-hidden shadow-lg shadow-red-500/10 ring-2 ring-red-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/20">
															<div className="relative aspect-video overflow-hidden bg-gradient-to-br from-red-100 to-red-50">
																{event.event.images[0] ? (
																	<img
																		src={`/resources/event-images/${event.event.images[0].id}`}
																		alt={
																			event.event.images[0].altText ||
																			event.event.name
																		}
																		className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
																	/>
																) : (
																	<div className="flex h-full items-center justify-center">
																		<Music className="h-16 w-16 text-red-300" />
																	</div>
																)}

																{/* Live Badge */}
																<div className="absolute right-3 top-3">
																	<Badge className="bg-gradient-to-r from-red-600 to-red-700 px-3 py-1.5 font-semibold text-white shadow-lg">
																		<div className="flex items-center space-x-1">
																			<div className="h-2 w-2 animate-pulse rounded-full bg-white"></div>
																			<span>LIVE NOW</span>
																		</div>
																	</Badge>
																</div>

																{/* Urgent CTA Overlay */}
																<div className="absolute bottom-3 left-3 right-3">
																	<div className="rounded-lg bg-black/70 px-3 py-2 text-center backdrop-blur">
																		<p className="text-sm font-medium text-white">
																			🎫 Tickets still available - Join now!
																		</p>
																	</div>
																</div>
															</div>

															<CardContent className="bg-gradient-to-br from-red-50/30 to-white p-4">
																<H3 className="mb-1 line-clamp-1 text-lg font-bold text-red-900">
																	{event.event.name}
																</H3>
																<div className="flex items-center text-sm text-gray-600">
																	<MapPin className="mr-1 h-3 w-3" />
																	<span>{event.event.nightclub.name}</span>
																</div>
																<div className="mt-2 text-lg font-bold text-red-600">
																	{priceDisplay}
																</div>
															</CardContent>
														</Card>
													</Link>
												)
											})}
										</div>

										{liveEvents.length > 3 && (
											<div className="mt-4 text-center">
												<Button
													variant="outline"
													className="border-red-200 text-red-600 hover:bg-red-50"
												>
													View all {liveEvents.length} live events
												</Button>
											</div>
										)}
									</div>
								)
							})()}

							<div className="mb-8 flex items-center justify-between">
								<div>
									<H2 className="text-3xl font-bold text-gray-900">
										All Events
									</H2>
									<P className="text-gray-600">
										{events.length} event{events.length !== 1 ? 's' : ''} found
									</P>
								</div>
								{(filters.search || filters.genre || filters.city) && (
									<Button variant="outline" onClick={() => setSearchParams({})}>
										Clear Filters
									</Button>
								)}
							</div>

							<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
								{events
									.sort((a, b) => {
										// Sort by status priority first (live events at top), then by date
										const statusA = getEventStatus(a)
										const statusB = getEventStatus(b)
										if (statusA.priority !== statusB.priority) {
											return statusA.priority - statusB.priority
										}
										return (
											new Date(a.startDate).getTime() -
											new Date(b.startDate).getTime()
										)
									})
									.map((event) => {
										const dateInfo = formatDate(
											event.startDate,
											event.event.nightclub.timezone,
										)
										const priceDisplay = getPriceDisplay(event)
										const status = getEventStatus(event)

										return (
											<Link
												key={event.id}
												to={`/events/${event.event.id}`}
												className="block"
											>
												<Card
													className={`group h-full cursor-pointer overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
														status.isLive
															? 'shadow-lg shadow-red-500/10 ring-2 ring-red-500/30'
															: ''
													}`}
												>
													{/* Event Image */}
													<div className="relative aspect-video overflow-hidden bg-gradient-to-br from-purple-100 to-blue-100">
														{event.event.images[0] ? (
															<img
																src={`/resources/event-images/${event.event.images[0].id}`}
																alt={
																	event.event.images[0].altText ||
																	event.event.name
																}
																className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
															/>
														) : (
															<div className="flex h-full items-center justify-center">
																<Music className="h-16 w-16 text-purple-300" />
															</div>
														)}

														{/* Date Badge */}
														<div className="absolute left-4 top-4">
															<div className="rounded-lg bg-white/90 p-2 text-center shadow-lg backdrop-blur">
																<div className="text-lg font-bold text-gray-900">
																	{dateInfo.dayMonth}
																</div>
																<div className="text-xs text-gray-600">
																	{dateInfo.weekday}
																</div>
															</div>
														</div>

														{/* Status Badge */}
														<div className="absolute right-4 top-4 z-20">
															{status.isLive ? (
																<div className="relative">
																	<Badge
																		variant="default"
																		className="border border-red-500/50 bg-gradient-to-r from-red-600 to-red-700 px-3 py-1.5 font-semibold text-white shadow-lg"
																	>
																		<div className="flex items-center space-x-1">
																			<div className="h-2 w-2 animate-pulse rounded-full bg-white"></div>
																			<span>LIVE NOW</span>
																		</div>
																	</Badge>
																</div>
															) : (
																<Badge
																	variant={status.variant}
																	className="font-medium shadow-lg"
																>
																	{status.text}
																</Badge>
															)}
														</div>
													</div>

													<CardContent
														className={`p-6 ${status.isLive ? 'bg-gradient-to-br from-red-50/50 to-white' : ''}`}
													>
														{/* Event Title */}
														<H2
															className={`mb-2 line-clamp-2 text-xl font-bold ${status.isLive ? 'text-red-900' : ''}`}
														>
															{event.event.name}
															{status.isLive && (
																<span className="ml-2 inline-flex items-center">
																	<div className="h-2 w-2 animate-pulse rounded-full bg-red-500"></div>
																</span>
															)}
														</H2>

														{/* Venue and Location */}
														<div className="mb-3 flex items-center text-gray-600">
															<MapPin className="mr-2 h-4 w-4" />
															<span className="text-sm">
																{event.event.nightclub.name}
															</span>
														</div>

														{/* Date and Time */}
														<div className="mb-3 flex items-center text-gray-600">
															<Calendar className="mr-2 h-4 w-4" />
															<span className="text-sm">
																{dateInfo.full} at {dateInfo.time}{' '}
																{dateInfo.timezone}
															</span>
														</div>

														{/* Genres */}
														{event.event.genres.length > 0 && (
															<div className="mb-4 flex flex-wrap gap-1">
																{event.event.genres
																	.slice(0, 3)
																	.map((genre: any) => (
																		<Badge
																			key={genre.name}
																			variant="outline"
																			className="text-xs"
																		>
																			{genre.name}
																		</Badge>
																	))}
																{event.event.genres.length > 3 && (
																	<Badge variant="outline" className="text-xs">
																		+{event.event.genres.length - 3}
																	</Badge>
																)}
															</div>
														)}

														{/* Capacity and Utilization */}
														{event.hasTickets && (
															<div className="mb-4">
																<div className="mb-2 flex items-center justify-between text-sm">
																	<span className="text-gray-600">
																		{event.ticketStats.availableNow} /{' '}
																		{event.ticketStats.totalAvailable} available
																	</span>
																	<span className="font-medium">
																		{event.utilizationPercentage}% sold
																	</span>
																</div>
																<Progress
																	value={event.utilizationPercentage}
																	className="h-2"
																/>
															</div>
														)}

														{/* Price Display */}
														<div className="flex items-center justify-between">
															<div>
																<div className="text-lg font-bold text-purple-600">
																	{priceDisplay}
																</div>
																{event.hasTickets && (
																	<div className="text-xs text-gray-500">
																		{event.ticketStats.hasPaidTickets &&
																		event.ticketStats.hasFreeTickets
																			? 'Mixed pricing'
																			: event.ticketStats.hasFreeTickets
																				? 'Free entry'
																				: 'Paid event'}
																	</div>
																)}
															</div>

															<div className="text-sm font-medium text-purple-600">
																View Details →
															</div>
														</div>
													</CardContent>
												</Card>
											</Link>
										)
									})}
							</div>
						</>
					)}
				</Container>
			</section>

			{/* Footer CTA */}
			<section className="bg-gradient-to-r from-purple-600 to-blue-600 py-16">
				<Container>
					<div className="text-center text-white">
						<H2 className="mb-4 text-3xl font-bold">
							Ready to Create Your Own Events?
						</H2>
						<P className="mx-auto mb-6 max-w-2xl text-purple-100">
							Join our platform as a venue owner or event organizer and start
							selling tickets to thousands of party-goers!
						</P>
						<div className="flex flex-col justify-center gap-4 sm:flex-row">
							<Button asChild size="lg" variant="secondary">
								<Link to="/signup">Get Started</Link>
							</Button>
							<Button
								asChild
								size="lg"
								variant="outline"
								className="border-white text-white hover:bg-white hover:text-purple-600"
							>
								<Link to="/about">Learn More</Link>
							</Button>
						</div>
					</div>
				</Container>
			</section>
		</div>
	)
}
